import { Method, AxiosError } from 'axios';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { useApiContext } from 'src/compass/site/providers';
import { onErrorDefaultHandler } from 'src/compass/shared/util/onErrorHandlers';

interface IUseAPI {
  method: Method;
  url: string;
  data?: Object;
  staleTime?: number;
  gcTime?: number;
  enabled?: boolean;
  onSuccess?: Function;
  onError?: (error: AxiosError<any>) => any;
  queryKey?: string[];
  params?: Record<string, any>;
  refetchInterval?: number | false;
  // Opt-in per query: reactQuery.config.ts turns refetchOnWindowFocus off globally. Leaving it
  // undefined keeps that default - but only because of how it is forwarded below; see there.
  refetchOnWindowFocus?: boolean;
  placeholderData?: (previousData: any) => any;
}

export function useAPI({
  method,
  url,
  data = {},
  staleTime = 0,
  gcTime,
  enabled = true,
  onSuccess,
  onError = onErrorDefaultHandler,
  queryKey,
  params,
  refetchInterval,
  refetchOnWindowFocus,
  placeholderData,
}: IUseAPI): UseQueryResult<any, AxiosError> {
  const { api } = useApiContext();

  return useQuery({
    queryKey: queryKey || [url, { data, params }],
    queryFn: async () => {
      try {
        const response = await api({ method, url, data, params });
        if (onSuccess) return onSuccess(response.data);
        return response.data;
      } catch (error) {
        return Promise.reject(onError(error));
      }
    },
    staleTime,
    gcTime,
    enabled,
    refetchInterval,
    // Included ONLY when the caller actually set it. React Query merges a query's options over
    // the client defaults with a plain spread, so a key that is *present but undefined* still
    // replaces the global refetchOnWindowFocus:false from reactQuery.config.ts - and
    // shouldFetchOn then reads undefined as "yes" (`value !== false && isStale`). Since
    // staleTime defaults to 0 here, every query is always stale, so passing the key
    // unconditionally silently re-enabled focus refetching for every query in the app: coming
    // back to the tab reloaded each table and blanked any open dialog to a loader (the dialog
    // detail query is what CasEditableDialog / SceReportsDetailsDialog gate isFetching on).
    // Any future option added here that the client defaults also set needs the same treatment.
    ...(refetchOnWindowFocus !== undefined && { refetchOnWindowFocus }),
    placeholderData,
  });
}
